import {createSlice} from '@reduxjs/toolkit'
import type {PayloadAction, UnknownAction} from '@reduxjs/toolkit'
import type {ProgramInterface} from '@shared/interfaces/program'

/**
 * Programs whose batch-relevant settings changed this session, grouped per equipment, so the
 * Programs tab can offer ONE batch recompute per equipment (AB#6595). Batches are only detected
 * from the checkpoint on, so a new or changed program has nothing to match before it unless the
 * user recomputes; the recompute itself is destructive, so it is only ever offered, never run.
 */
export type BatchRecomputeOffer = {
  equipmentId: string
  equipmentName: string
  lineId: string
  programIds: string[]
}

type State = {pending: BatchRecomputeOffer[]}

const initialState: State = {pending: []}

// Program fields batch & program detection reads; changing anything else (name, description,
// colour, classification, anomaly detection, trace settings...) leaves the batches as they are.
// Condition groups are written through their own endpoints and always end in
// updateProgramConditions, which is matched separately.
export const BATCH_RELEVANT_PROGRAM_FIELDS = [
  'is_enabled',
  'triggers_truth_eval',
  'batch_changeover_signals',
  'batch_changeover_period',
  'batch_changeover_timezone',
  'step_signal',
  'machine_sub_system_id',
]

const getBodyKeys = (body: Partial<ProgramInterface> | FormData): string[] =>
  body instanceof FormData ? Array.from(body.keys()) : Object.keys(body)

export const isBatchRelevantProgramChange = (
  method: string,
  body: Partial<ProgramInterface> | FormData,
): boolean => {
  if (method === 'POST') return true
  if (method !== 'PATCH' && method !== 'PUT') return false
  return getBodyKeys(body).some((key) => BATCH_RELEVANT_PROGRAM_FIELDS.includes(key))
}

type ProgramMutationFulfilled<Args> = UnknownAction & {
  payload: {result?: ProgramInterface}
  meta: {arg: {endpointName: string; originalArgs: Args}}
}

// Matches a successful RTK Query mutation by endpoint name. Deliberately not
// `lineManagementAPI.endpoints.*.matchFulfilled`: importing the API module into the store would make
// every test that mocks '@apis/lineManagement' without that export fail to build the store.
const isFulfilledMutation =
  <Args>(endpointName: string) =>
  (action: UnknownAction): action is ProgramMutationFulfilled<Args> =>
    action.type.endsWith('/executeMutation/fulfilled') &&
    (action as Partial<ProgramMutationFulfilled<Args>>).meta?.arg?.endpointName === endpointName

const recordProgram = (state: State, program: ProgramInterface | undefined): void => {
  const equipment = program?.machine_sub_system
  if (!program?.id || !equipment?.id) return
  const offer = state.pending.find((pending) => pending.equipmentId === equipment.id)
  if (offer) {
    if (!offer.programIds.includes(program.id)) offer.programIds.push(program.id)
    return
  }
  state.pending.push({
    equipmentId: equipment.id,
    equipmentName: equipment.name,
    lineId: program.machine,
    programIds: [program.id],
  })
}

export const batchRecomputeOffer = createSlice({
  name: 'batchRecomputeOffer',
  initialState,
  reducers: {
    // "Later" and a confirmed recompute both clear the offer; the next change offers again.
    dismissBatchRecomputeOffer: (state, action: PayloadAction<string>) => {
      state.pending = state.pending.filter((offer) => offer.equipmentId !== action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isFulfilledMutation<{method: string; body: Partial<ProgramInterface> | FormData}>(
          'updateProgram',
        ),
        (state, action) => {
          const {method, body} = action.meta.arg.originalArgs
          if (isBatchRelevantProgramChange(method, body))
            recordProgram(state, action.payload.result)
        },
      )
      .addMatcher(isFulfilledMutation('updateProgramConditions'), (state, action) =>
        recordProgram(state, action.payload.result),
      )
  },
})

export const {dismissBatchRecomputeOffer} = batchRecomputeOffer.actions
export default batchRecomputeOffer.reducer
